import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { featuredParts, type Part } from '../../../mocks/products';

interface FeaturedProductsProps {
  title?: string;
  subtitle?: string;
  products?: Part[];
}

const filterTabs = ['All Parts', 'Vertical Blind', 'Horizontal Blind', 'Mini Blind', 'Hardware', 'Chains & Cords'];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`text-[11px] ${
            s <= Math.round(rating) ? 'ri-star-fill text-amber-500' : 'ri-star-line text-stone-300'
          }`}
        />
      ))}
    </div>
  );
}

function PartCard({ part }: { part: Part }) {
  const [wished, setWished] = useState(false);

  return (
    <div className="group bg-white rounded-lg border border-stone-200 hover:border-amber-300 transition-all duration-200 overflow-hidden">
      {/* Image */}
      <div className="relative w-full h-52 bg-stone-100 overflow-hidden">
        <img
          src={part.image}
          alt={part.name}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
        />
        {part.badge && (
          <span className="absolute top-3 left-3 bg-amber-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-sm uppercase tracking-wide">
            {part.badge}
          </span>
        )}
        {!part.inStock && (
          <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center">
            <span className="text-white font-bold text-sm">Out of Stock</span>
          </div>
        )}
        <button
          onClick={() => setWished(!wished)}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-colors cursor-pointer"
        >
          <i className={`text-base ${wished ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-stone-400'}`} />
        </button>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="text-stone-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
          SKU: {part.sku}
        </div>
        <h3 className="font-bold text-stone-900 text-sm leading-snug mb-2 group-hover:text-amber-700 transition-colors">
          {part.name}
        </h3>
        <div className="flex items-center gap-2 mb-1">
          <StarRating rating={part.rating} />
          <span className="text-stone-400 text-[11px]">({part.reviewCount})</span>
        </div>
        <p className="text-stone-500 text-xs leading-snug mb-4 line-clamp-2">{part.description}</p>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-stone-900 font-bold text-lg">${part.price.toFixed(2)}</span>
            {part.originalPrice && (
              <span className="text-stone-400 text-sm line-through ml-2">${part.originalPrice.toFixed(2)}</span>
            )}
          </div>
          <button
            disabled={!part.inStock}
            className="bg-amber-600 hover:bg-amber-500 disabled:bg-stone-300 text-white text-xs font-bold px-4 py-2.5 rounded-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-shopping-cart-2-line text-sm" />
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedProducts({
  title = 'Best-Selling Replacement Parts',
  subtitle = 'Top-rated parts ordered by thousands of customers. Ships within 1–2 business days.',
  products,
}: FeaturedProductsProps) {
  const [activeTab, setActiveTab] = useState('All Parts');
  const navigate = useNavigate();
  const allProducts = products || featuredParts;

  const filtered = activeTab === 'All Parts'
    ? allProducts
    : allProducts.filter((p) => {
        if (activeTab === 'Vertical Blind') return p.categorySlug === 'vertical-blind-parts';
        if (activeTab === 'Horizontal Blind') return p.categorySlug === 'horizontal-blind-parts';
        if (activeTab === 'Mini Blind') return p.categorySlug === 'mini-blind-parts';
        if (activeTab === 'Hardware') return p.categorySlug === 'mounting-brackets' || p.categorySlug === 'drapery-hardware' || p.categorySlug === 'valance-clips';
        if (activeTab === 'Chains & Cords') return p.categorySlug === 'chains-cords';
        return true;
      });

  return (
    <section id="products" className="bg-stone-50 py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="inline-block text-amber-600 text-xs font-bold tracking-widest uppercase mb-2">
              Best Sellers
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-stone-900">{title}</h2>
            <p className="text-stone-500 mt-2 text-sm">{subtitle}</p>
          </div>
          <button
            onClick={() => navigate('/category/vertical-blind-parts')}
            className="bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold px-6 py-3 rounded-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap self-start sm:self-auto"
          >
            View Full Catalog
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap mb-8">
          {filterTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-xs font-semibold rounded-sm tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-amber-600 text-white'
                  : 'bg-white border border-stone-200 text-stone-600 hover:border-amber-400 hover:text-amber-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {(filtered.length > 0 ? filtered : allProducts).map((part) => (
            <PartCard key={part.id} part={part} />
          ))}
        </div>

        {/* CTA row */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/category/vertical-blind-parts')}
            className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm px-10 py-4 rounded-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
          >
            Browse Full Parts Catalog
            <i className="ri-arrow-right-line" />
          </button>
          <p className="text-stone-400 text-xs mt-3">1,000+ parts available · Fast shipping nationwide</p>
        </div>
      </div>
    </section>
  );
}
